/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
import { assign, createMachine, Interpreter, State } from 'xstate-v4'
import { serializeError } from '~/utility/xstate/serializeError'
import { getDefaultSearchFilters } from '../defaults'
import { SearchContext, SearchFilters } from '../schemas'

export type SearchNonPriceEvent = {
  type: 'search'
  filters: SearchFilters
  searchUrl?: string
}
export type SearchPriceEvent = { type: 'priceSearch'; filters: SearchFilters }
export type SearchStateEvent =
  | SearchPriceEvent
  | SearchNonPriceEvent
  | { type: 'CLEAR_RESULTS' }

export function getSearchMachine() {
  return createMachine<SearchContext, SearchStateEvent>(
    {
      id: 'search',
      initial: 'idle',
      context: {
        filters: getDefaultSearchFilters(),
        error: undefined,
      },
      on: {
        search: 'searching',
        priceSearch: 'priceSearching',
        CLEAR_RESULTS: {
          target: 'idle',
          actions: ['clearError', 'clearResults'],
        },
      },
      states: {
        idle: {},
        searching: {
          entry: assign({
            filters: (_, { filters }) => filters,
          }),
          invoke: {
            src: 'doSearch',
            onDone: [
              {
                actions: ['setResults', 'setRedirectPath'],
                target: 'redirecting',
                cond: 'isSeoRedirect',
              },
              {
                actions: 'setResults',
                target: 'results',
                cond: 'pathIsCorrect',
              },
              {
                actions: ['setResults', 'setRedirectPath'],
                target: 'redirecting',
              },
            ],

            onError: [
              {
                // skip errors from abort
                target: 'idle',
                cond: (_context, event) => {
                  return event.data.cause?.code === 20
                },
              },
              {
                // Route 404s to notFound state
                target: 'notFound',
                cond: (_context, event) => {
                  return event.data?.response?.status === 404
                },
                actions: assign({
                  error: ({ error }) => serializeError(error),
                }),
              },
              {
                target: 'error',
                actions: assign({
                  error: ({ error }) => serializeError(error),
                }),
              },
            ],
          },
        },
        priceSearching: {
          entry: assign({
            filters: (_, { filters }) => filters,
          }),
          invoke: {
            src: 'doPriceSearch',
            onDone: {
              target: 'results',
              actions: 'setResults',
            },

            onError: [
              {
                // skip errors from abort
                target: 'idle',
                cond: (_context, event) => {
                  return event.data.cause?.code === 20
                },
              },
              {
                // Route 404s to notFound state
                target: 'notFound',
                cond: (_context, event) => {
                  return event.data?.response?.status === 404
                },
                actions: assign({
                  error: ({ error }) => serializeError(error),
                }),
              },
              {
                target: 'error',
                actions: assign({
                  error: ({ error }) => serializeError(error),
                }),
              },
            ],
          },
        },
        redirecting: {},
        results: {},
        error: {
          onEntry: ['setError', 'logError'],
          onExit: ['clearError'],
        },
        notFound: {
          onEntry: ['setError', 'logError'],
          onExit: ['clearError'],
        },
      },
    },
    {
      actions: {
        clearError: assign((_) => {
          return { errorMessage: undefined }
        }),

        setError: assign((_, { data }: any) => {
          return { error: serializeError(data), errorMessage: data.toString() }
        }),
        setResults: assign((_, { data }: any) => {
          return { results: data }
        }),
        clearResults: assign((_) => {
          return {
            results: undefined,
            filters: getDefaultSearchFilters(),
          }
        }),
        setRedirectPath: assign((_, { data }: any) => {
          return { redirectToPath: data.path }
        }),

        logError: (data) => {
          // Only log serializable error details to avoid Nuxt stringify errors
          const errorMessage =
            data.error instanceof Error
              ? data.error.message
              : String(data.error)
          const statusCode = data.error?.statusCode
          if (statusCode?.toString().startsWith('5')) {
            console.error('Search error:', {
              statusCode,
              message: errorMessage,
            })
          } else {
            // console.warn used for Sentry breadcrumbs
            // eslint-disable-next-line no-console -- Sentry breadcrumb tracking
            console.warn('Search warning:', {
              statusCode,
              message: errorMessage,
            })
          }
        },
      },
      guards: {
        pathIsCorrect: ({ filters }, { data }: any) => {
          if (!data.path) {
            return true
          }
          return !!filters.hierarchyPath && filters.hierarchyPath === data.path
        },
        isSeoRedirect: ({ filters }, { data }: any) => {
          if (data.seoRedirect && filters.base) {
            const isSeoBase = !['search', 'map'].includes(filters.base)
            return isSeoBase
          }
          return false
        },
      },
      services: {
        doSearch: () => {
          throw new Error('doSearch Not Implemented')
        },
        doPriceSearch: () => {
          throw new Error('doSearch Not Implemented')
        },
      },
    },
  )
}

export type SearchState = State<SearchContext, SearchStateEvent>
export type SearchMachineInterpreter = Interpreter<
  SearchContext,
  SearchState,
  SearchStateEvent
>
